import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useTheme } from "@/components/ThemeProvider";
import { SaaviLogo } from "@/components/SaaviLogo";
import { NAV_LINKS } from "@/lib/nav";
import { cn } from "@/lib/utils";

function Logo() {
  return (
    <Link to="/" data-testid="logo-link" className="group">
      <span className="md:hidden"><SaaviLogo size={52} /></span>
      <span className="hidden md:inline-flex"><SaaviLogo size={72} /></span>
    </Link>
  );
}

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all",
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-b border-border"
          : "bg-background/40 backdrop-blur-md border-b border-transparent"
      )}
    >
      <div className="container-saavi flex h-20 md:h-24 items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "relative px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute inset-x-3 -bottom-0.5 h-0.5 bg-saavi-blue" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to="/contact" className="hidden md:inline-flex">
            <Button data-testid="navbar-cta-contact" className="h-10 rounded-md gap-1">
              Book a Call <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-border"
              >
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] sm:w-[420px] p-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex h-full flex-col">
                <div className="border-b border-border px-6 py-5"><SaaviLogo size={56} /></div>
                <nav className="flex-1 overflow-y-auto px-3 py-4">
                  {NAV_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center justify-between px-4 py-3 rounded-md text-base font-medium",
                          isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                        )
                      }
                    >
                      <span>{l.label}</span>
                      <ArrowUpRight className="h-4 w-4 opacity-60" />
                    </NavLink>
                  ))}
                </nav>
                <div className="border-t border-border p-6">
                  <Link to="/contact"><Button className="w-full h-11" data-testid="mobile-cta-contact">Book a Free Strategy Call</Button></Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
